import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import { createStore, applyMiddleware, compose } from "redux";
import thunk from "redux-thunk";
import promiseMiddleware from "redux-promise";
import { autoRehydrate } from "redux-persist";
import "babel-polyfill";

import Config from "@skin_config";
import Honeybadger from "honeybadger-js";
import ErrorBoundary from "@honeybadger-io/react";
import { TicketsContextProvider } from "@tickets/store/context";
import allReducers from "../reducers";
import Bootstrap from "./bootstrap";
import { gaMiddleWare } from "../helpers/analytics";
import RouterBuilder from "../components/router/index";
import Loader from "@common/components/loader";
import Helpers from "@common/helpers/helperFunctions";
import terminalMiddleWare from "../middleware/terminalMiddleware";
import { FavoritesContextProvider } from "../contexts/favorites";
import { SelectionsContextProvider } from "@common/contexts/selections";
import { SwarmContextProvider } from "@lbs/swarm-client";
import { checkForWebsitePass } from "../helpers/secure";
import { PreferencesContextProvider } from "@common/contexts/preferences";
import { PopupContextProvider } from "../contexts/popup";
import { RacingsContextProvider } from "@racings/store/context";
import { YMInitializer } from "react-yandex-metrika";
import { ENVIRONMENT_MODES } from "../constants/environmentModes";
import { UIContextProvider } from "../contexts/ui";
import CrashError from './crashError';
import ScawScript from '@app/dev/components/scawAnimation';
import { getTerminalLanguage } from "../helpers/languageByIdOrConfig";
// ---------------------- Import skin custom JS if present
try {
  // In development: use relative path, in production: use absolute path from dist
  const skinExportsPath = import.meta.env.DEV
    ? `../../skins/${__SKIN__}/terminal/js/index.jsx`
    : `/skins/${__SKIN__}/terminal/js/index.jsx`;

  const skinExportsModule = await import(/* @vite-ignore */ skinExportsPath);
  Config.env.skinExports = skinExportsModule.default || skinExportsModule;
} catch (error) {
  Config.env.skinExports = {};
  console.log('Skin custom JS not found or failed to load');
}

// ---------------------- Import default SCSS
const scssModules = import.meta.glob('../terminal/scss/defaults/**/*.scss', { eager: true });
console.log('Loaded default SCSS files:', Object.keys(scssModules).length);

const timer = 2000;

const composeEnhancers =
  process.env.NODE_ENV === "development" ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose : compose;

const middlewares = [terminalMiddleWare, thunk, promiseMiddleware, gaMiddleWare];

if (Helpers.CheckIfPathExists(Config, "isPartnerIntegration.mode.iframe")) {
  middlewares.push(handleApiIntegrationPartnersActions);
  Helpers.addClassName(".main-wrapper", "integration-view");
}
export const store = createStore(allReducers, composeEnhancers(applyMiddleware(...middlewares), autoRehydrate()));

checkForWebsitePass().then(() => {
  // Do the app bootstrapping and inform store that application is ready to run
  // Layout component will render it's children only when application is ready
  Bootstrap(store);

  if (process.env.NODE_ENV === "production") {
    Honeybadger.configure({
      apiKey: "hbp_J7VUik9BPGDvWAgsOHF2BN8rQ6ztzh142eot",
      environment: process.env.NODE_ENV,
    });

    Honeybadger.setContext({
      Terminal_Id: sessionStorage.getItem("authData") && JSON.parse(sessionStorage.getItem("authData")).tid,
      Partner_Id: sessionStorage.getItem("authData") && JSON.parse(sessionStorage.getItem("authData")).pid,
    });
  }

  function CloseButton({ closeToast }) {
    return <i className="global-icon icon-Close" onClick={closeToast} />;
  }
  const isProductionMode = _MODE_ === ENVIRONMENT_MODES.PRODUCTION;
  const terminalLang = getTerminalLanguage();
  let inited;
  const unsubscribe = store.subscribe(() => {
    console.log('app ready', store.getState().appReady)
    switch (true) {
      case store.getState().appReady:
        unsubscribe();
        const { startupSettings } = store.getState().terminal;
        startupSettings?.HideLogo && Helpers.addClassName(".main-wrapper", "hideLogo");
        ReactDOM.render(
          <ErrorBoundary honeybadger={Honeybadger} ErrorComponent={CrashError}>
            <>
              {Config.main.yandexMetricaId && isProductionMode && (
                <YMInitializer accounts={[Config.main.yandexMetricaId]} options={{ webvisor: true }} version="2" />
              )}
              <Provider store={store}>
                <SwarmContextProvider>
                  <PreferencesContextProvider config={Config} lang={terminalLang}>
                    <ToastContainer
                      position="top-right"
                      autoClose={timer}
                      hideProgressBar={false}
                      newestOnTop
                      enableMultiContainer
                      containerId="Main-Toastify"
                      theme="dark"
                      closeOnClick
                      closeButton={CloseButton}
                      rtl={false}
                      pauseOnHover
                    />
                    <SelectionsContextProvider config={Config}>
                      <UIContextProvider>
                        <FavoritesContextProvider>
                          <PopupContextProvider>
                            <RacingsContextProvider config={Config}>
                              <TicketsContextProvider>
                                <RouterBuilder store={store} />
                              </TicketsContextProvider>
                            </RacingsContextProvider>
                          </PopupContextProvider>
                        </FavoritesContextProvider>
                      </UIContextProvider>
                    </SelectionsContextProvider>
                  </PreferencesContextProvider>
                </SwarmContextProvider>
              </Provider>
            </>
          </ErrorBoundary>,
          document.getElementById("root")
        );
        break;
      case !inited:
        inited = true;
        ReactDOM.render(
          <PreferencesContextProvider config={Config}>
            <div className="app-loading">
              <Loader />
              <ScawScript />
            </div>
          </PreferencesContextProvider>,
          document.getElementById("root")
        );
        break;
      default:
        break;
    }
  });
});
